import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AlertMessagesService, MediaOpsService, NoDataComponent, OmniIconComponent, Utils } from '../../../../../shared';
import CampaignOverviewSection from './CampaignOverviewSection';
import MediaOrdersTable from './MediaOrdersTable';
import CreateMediaOrderWizard from './CreateMediaOrderWizard';
import { CampaignDetailService } from '../../../services/campaignDetails-service';
import CampaignDetailInfoCards from './CampaignDetailInfoCards';

const CampaignDetailComponentV2 = (params) => {
    const [campaignData, setCampaignData] = useState(null);
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        searchText: '',
        selectedFilters: []
    });
    const [clientCodes, setClientCodes] = useState([]);
    const [childCampaigns, setChildCampaigns] = useState(null);
    const [orderedData, setOrderedData] = useState([]);
    const [displayTableSkeleton, setDisplayTableSkeleton] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const mediaOrdersRenderRef = useRef(false);
    const savedScrollPositionRef = useRef(null);

    // Memoize enriched campaign data with computed fields
    const enrichedCampaignData = useMemo(() => {
        if (!campaignData?.[0]) return null;
        
        const campaign = campaignData[0];
        return {
            ...campaign,
            campaignName: campaign.campaign_display_name,
            dateRange: campaign.campaignstart && campaign.campaignend
                ? `${Utils.convertDateToDisplayFormat(campaign.campaignstart)} - ${Utils.convertDateToDisplayFormat(campaign.campaignend)}`
                : null,
            mediaPlanSource: campaign.data_source,
            syntheticId: campaign.synthetic_id,
            productCode: campaign.productid,
            clientCode: campaign.client_code,
            planVersion: `${campaign.filerevision}(${Utils.convertToYYYYMMDD(campaign.file_date)})`,
            lastModified: Utils.convertToYYYYMMDD(campaign.updated_at, true),
            campaignFromRoute: params.state?.campaignObj
        };
    }, [campaignData]);

    /**
     * Renders skeleton loader for campaign overview cards
     */
    const renderOverviewSkeleton = () => {
        return (
            <div className="campaign-overview-section">
                <div className="campaign-overview-cards">
                    {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="skeleton-card">
                            <div className="skeleton-header">
                                <div className="skeleton-line skeleton-title"></div>
                            </div>
                            <div className="skeleton-card-body">
                                <div className="skeleton-chart-placeholder">
                                    <div className="skeleton-bar"></div>
                                    <div className="skeleton-bar"></div>
                                    <div className="skeleton-bar"></div>
                                    <div className="skeleton-bar"></div>
                                    <div className="skeleton-bar"></div>
                                </div>
                                <div className="skeleton-data-section">
                                    <div className="skeleton-line skeleton-data-item"></div>
                                    <div className="skeleton-line skeleton-data-item"></div>
                                    <div className="skeleton-line skeleton-data-item"></div>
                                    <div className="skeleton-line skeleton-data-item"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /**
     * Renders skeleton loader for media orders table
     */
    const renderTableSkeleton = () => {
        return (
            <div className="media-orders-table-skeleton">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                    <div key={row} className="skeleton-table-row">
                        <div className="skeleton-cell skeleton-cell-small"></div>
                        <div className="skeleton-cell skeleton-cell-large"></div>
                        <div className="skeleton-cell skeleton-cell-medium"></div>
                        <div className="skeleton-cell skeleton-cell-small"></div>
                        <div className="skeleton-cell skeleton-cell-medium"></div>
                        <div className="skeleton-cell skeleton-cell-medium"></div>
                        <div className="skeleton-cell skeleton-cell-medium"></div>
                        <div className="skeleton-cell skeleton-cell-small"></div>
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const initializeComponent = async () => {

            if (params?.state?.campaignObj?.parent_id) {
                setActiveTab('mediaOrders');
            }
            
            // Reset spinner at start
            params.setSpinnerCount(0);

            // Fetch client codes in parallel (independent operation)
            // fetchClientCodes();  // Uncomment if client codes are needed in the component
            let hasCampaignInState = false;
            const hasChildCampaignsInState = params.state?.selectedChildCampaigns?.length > 0;

            if (!params.state?.campaignObj?.parent_id) {
                hasCampaignInState = Object.keys(params.state?.campaignObj || {}).length > 0;
            }

            // Scenario 1: No campaign data in state - fetch everything
            if (!hasCampaignInState) {
                await fetchCampaignDetails();
                return;
            }

            // Scenario 2: Has campaign data in state
            setCampaignData([params.state.campaignObj]);
            params.setCampaignName(Utils.formatCampaignTitle(params.state.campaignObj))
            if (params?.systemParametersData?.data?.buyingSystem?.toLowerCase() !== 'none') {
                getOrderedData(params.state.campaignObj);
            }

            // Scenario 2a: Has child campaigns in state - use them
            if (hasChildCampaignsInState) {
                setChildCampaigns(params.state.selectedChildCampaigns);
                setDisplayTableSkeleton(false);
                return;
            }

            // Scenario 2b: No child campaigns in state - fetch them
            await fetchChildCampaigns(params.state.campaignObj);
        };

        initializeComponent();

        return () => {
            setCampaignData(null);
            setChildCampaigns(null);
            setDisplayTableSkeleton(true);
        };
    }, []);


    const getOrderedData = (campaignParam) => {
        let campaignId;
        let campaignSyntheticId = '';
        if (params.state?.campaignObj?.parent_id) {
            campaignId = encodeURIComponent(campaignParam?.id);
            campaignSyntheticId = campaignParam?.synthetic_id;
        } else {
            campaignId = params.state?.campaignObj?.id ? encodeURIComponent(params.state?.campaignObj?.id) : params?.params?.campaignId;
            campaignSyntheticId = params.state?.campaignObj?.synthetic_id;
        }

        CampaignDetailService.getOrderedData(campaignId,campaignSyntheticId).then((response) => {
            if(!response?.data?.length){
                setOrderedData(null);
                AlertMessagesService.showMultipleAlerts([{ type: 'warning', title: 'Ordered data not found', bodyMsgArr: ['Ordered data is not available for this campaign.'] }]);
            }else{
                setOrderedData(response?.data[0]);
            }
        }).catch((error) => {
            AlertMessagesService.showMultipleAlerts([{
                type: 'danger',
                retryBtnText: 'Retry',
                retryBtnEvent: () => getOrderedData(campaignParam),
                title: 'Error fetching ordered data',
                bodyMsgArr: [error?.message]
            }]);
        });
    }

    const updateMediaOrdersRenderRef = () => {
        mediaOrdersRenderRef.current = true;
    }

    const updateSavedScrollPositionRef = (position) => {
        savedScrollPositionRef.current = position;
    }

    const implementSavedScrollPosition = () => {
        if (savedScrollPositionRef.current) {
            setTimeout(() => {
                window.scrollTo({
                    top: savedScrollPositionRef.current.y,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }

    const fetchCampaignDetails = async () => {
        setDisplayTableSkeleton(true);
        let campaignId;

        if (params.state?.campaignObj?.parent_id) {
            campaignId = encodeURIComponent(params.state?.campaignObj?.parent_id);
        } else {
            campaignId = params.state?.campaignObj?.id ? encodeURIComponent(params.state?.campaignObj?.id) : params?.params?.campaignId;
        }

        MediaOpsService.getCampaignDataById(campaignId, params.state?.campaignObj?.client_id, params?.versionOneApi).then((campaignData) => {
            setCampaignData(campaignData);
            params.setCampaignName(Utils.formatCampaignTitle(campaignData[0]));

            // Update location state with parent campaigns, if the current campaign is a child campaign.
            if (params.navigate && params.location && params.state?.campaignObj?.parent_id) {
                params.navigate(params.location.pathname, {
                    replace: true,
                    state: {
                        ...params.state,
                        parentCampaign: campaignData[0]
                    }
                });
            }
            if (params?.systemParametersData?.data?.buyingSystem?.toLowerCase() !== 'none') {
                getOrderedData(campaignData[0]);
            }
            if (campaignData[0]?.child_count) {
                fetchChildCampaigns(campaignData[0]); // todo update after backend changes in single campaign api call    
            } else {
                setDisplayTableSkeleton(false);
                implementSavedScrollPosition();
            }
        }).catch((error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: fetchCampaignDetails, title: 'Error fetching campaign details', bodyMsgArr: [error?.message] }]);
            setDisplayTableSkeleton(false);
        });
    };

    const fetchClientCodes = () => {
        MediaOpsService.getClientCodes().then(
          (response) => {
            let clientCodes = [...response?.data?.client_codes]
            setClientCodes(clientCodes);
          }
        ).catch((error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: fetchClientCodes, title: 'Error fetching client codes', bodyMsgArr: [error?.message] }]);
        });
      };

    const fetchChildCampaigns = async (parentOrChildCampaign) => {
        setDisplayTableSkeleton(true);
        const payload = {
            'source': 'children',
            'parent-id': parentOrChildCampaign?.id,
            'campaign-id': parentOrChildCampaign?.synthetic_id,
            'client-id': Utils.getRouteParam("clientid")
        };

        if (parentOrChildCampaign?.parent_id) {
            fetchCampaignDetails();
            return;
        }

        MediaOpsService.getCampaignData(payload, null, false).then((childCampaignsData) => {
            const childCampaigns = childCampaignsData?.data?.Items || [];
            const preparedChildCampaigns = MediaOpsService.prepareCampaignData(childCampaigns);
            setChildCampaigns(preparedChildCampaigns);
            implementSavedScrollPosition();
        }).catch((error) => {
            const retryCallback = () => fetchChildCampaigns(parentOrChildCampaign);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: retryCallback, title: 'Error fetching child campaigns', bodyMsgArr: [error?.message] }]);
        }).finally(() => {
            setDisplayTableSkeleton(false);
        });

    };

    const handleOpenWizard = () => {
        setIsWizardOpen(true);
    };

    const handleCloseWizard = () => {
        setIsWizardOpen(false);
    };

    const handleWizardSubmit = async (formData) => {

        let selectedChannel = formData.selectedChannels.map(channelData => {
            return {
                channel_name: channelData?.channel?.originalData?.channel_name,
                channel_startdate: channelData?.channel?.originalData?.channel_startdate || null,
                channel_enddate: channelData?.channel?.originalData?.channel_enddate || null,
                channel_total_budget: channelData?.channel?.originalData?.channel_total_budget,
                tier_id: channelData?.channel?.originalData?.channel_tier_id,
                publishers: channelData?.publishers?.map(publisher => {
                    return {
                        publisher_name: publisher?.originalData.publisher_name,
                        publisher_budget: publisher?.availablePlannedSpend,
                        publisher_type: publisher?.originalData.publisher_type,
                        publisher_start_date: publisher?.originalData.publisher_start_date || null,
                        publisher_end_date: publisher?.originalData.publisher_end_date || null
                    };
                })
            }
        });
        
        let createPayload = {
            'campaign_name': campaignData[0]?.campaignName,
            'campaign_taxonomy': formData.campaignTaxonomy,
            'campaign_taxonomy_display': formData.campaign_taxonomy_display,
            'campaign_startdate': campaignData[0]?.start_date,
            'campaign_enddate': campaignData[0]?.end_date,
            'taxonomy_schema': formData.taxonomy_schema,
            'client_code': formData.clientCode,
            'parent_id': campaignData[0]?.amd_campaign_id,
            'channels': selectedChannel,
            'is_currency_local': params?.systemParametersData?.data?.isLocal,
            'parent_campaign_total_budget': campaignData[0]?.campaign_total_budget,
            'parent_campaign_total_budget_local': campaignData[0]?.campaign_total_budget_local,
            'taxonomy': formData.taxonomy,
            'taxonomy_client_id': formData?.taxonomy_client_id,
            'taxonomy_schema_id': formData?.taxonomy_schema_id
        }

        let prismaPayload = {
            'campaignTaxonomy': formData.campaignTaxonomy,
            'campaignTaxonomyDisplay': formData.campaign_taxonomy_display,
            'taxonomySchema': formData.taxonomy_schema,
            'userEmail': params.omniProfile?.email || '',
            'channels': formData.selectedChannels.map(item => item.channel.originalData?.channel_name),
            'isCurrencyLocal': params?.systemParametersData?.data?.isLocal,
            'campaignStartDate': campaignData[0]?.start_date,
            'campaignEndDate': campaignData[0]?.end_date
        }

        let createMediaOrderPromise = CampaignDetailService.createChildCampaign(createPayload);

        params.setSpinnerCount(1);
        setIsWizardOpen(false);

        if(!formData.syncWithPrisma) {
            // Create media order without Prisma sync
            createMediaOrderPromise.then((response) => {
                AlertMessagesService.showSuccessToastMessage('Media Order created successfully.');
                fetchChildCampaigns(campaignData[0]);
            }).catch((error) => {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => handleWizardSubmit(formData), title: 'Error creating media order', bodyMsgArr: [error?.message] }]);
            }).finally(() => {
                params.setSpinnerCount(0);
            });
        } else {
            // Create media order with Prisma sync
            createMediaOrderPromise.then((newCampaign) => {
                // Sync with Prisma
                MediaOpsService.sendCampaignToMediaOcean(prismaPayload, newCampaign.data?.id, "V2").then((response) => {
                    AlertMessagesService.showSuccessToastMessage('Media Order created and synced with Prisma successfully.');
                }).catch((error) => {
                    AlertMessagesService.showWarningToastMessage('Media Order created but failed to sync with Prisma: ' + error.message);
                }).finally(() => {
                    fetchChildCampaigns(campaignData[0]);
                    params.setSpinnerCount(0);
                });
            }).catch((error) => {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => handleWizardSubmit(formData), title: 'Error creating media order', bodyMsgArr: [error?.message] }]);
                params.setSpinnerCount(0);
            })
        }
    };

    const handleFilterChange = (filters) => {
        setFilterOptions(filters);
    };


    return (<>
        <div className="campaign-detail-v3">

            {/* Campaign Detail info card with placement and creative */}
            {enrichedCampaignData ? (
                <CampaignDetailInfoCards
                    campaignData={enrichedCampaignData}
                    orderedData={orderedData}
                    navigate={params.navigate}
                />
            ) : ''}
            {/* Tabs Navigation */}
            <div className="campaign-detail-tabs">
                <button 
                    className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={`tab-button ${activeTab === 'mediaOrders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('mediaOrders')}
                >
                    Media Orders
                </button>
            </div>

            {/* Campaign Overview Section - Top cards and Plan Alignment */}
            {activeTab === 'overview' && (
                <>
                    {campaignData === null ? (
                        renderOverviewSkeleton()
                    ) : campaignData?.length === 0 ? (
                        <NoDataComponent
                            heading={"No Data Found"}
                            message={"No campaign data available"}
                        />
                    ) : (
                        <CampaignOverviewSection 
                            campaignData={campaignData[0]}
                            orderedData={orderedData}
                            systemParametersData={params.systemParametersData}
                        />
                    )}
                </>
            )}

            {/* Media Orders Section */}
            {activeTab === 'mediaOrders' && (
            <>
                {displayTableSkeleton ? (
                    <div className="media-orders-section">
                        <div className="media-orders-header">
                            <h2>Media Orders</h2>
                        </div>
                        <div key="mediaOrderTableWrapper" className="media-orders-table-wrapper">
                            {renderTableSkeleton()}
                        </div>
                    </div>
                ) : campaignData?.length > 0 && childCampaigns !== null && (
                <div className="media-orders-section">
                    <div className="media-orders-header">
                        <h2>Media Orders</h2>
                        <div className="media-orders-actions">
                            <OmniIconComponent className="is-size-2 add-icon" icon-id="omni:interactive:add" onClick={handleOpenWizard} />
                        </div>
                    </div>

                    <div key="mediaOrderTableWrapper" className="media-orders-table-wrapper">
                        <MediaOrdersTable
                            key={campaignData[0]?.id}
                            campaignObj={campaignData[0]}
                            filters={filterOptions}
                            campaignTaxonomyData={params.campaignTaxonomyData}
                            systemParametersData={params.systemParametersData}
                            navigate={params.navigate}
                            childCampaigns={childCampaigns}
                            omniProfile={params.omniProfile}
                            versionOneApi={params?.versionOneApi}
                            setSpinnerCount={params.setSpinnerCount}
                            fetchCampaignDetails={fetchCampaignDetails}
                            locationState={params.state}
                            mediaOrdersRenderRef={mediaOrdersRenderRef}
                            updateMediaOrdersRenderRef={updateMediaOrdersRenderRef}
                            updateSavedScrollPositionRef={updateSavedScrollPositionRef}
                            orderedData={orderedData}
                        />
                    </div>
                </div>
                )}
            </>
            )}

            {/* Media Order Creation Wizard */}
            {isWizardOpen && (
                <CreateMediaOrderWizard
                    isOpen={isWizardOpen}
                    campaignTaxonomyData={params.campaignTaxonomyData}
                    onClose={handleCloseWizard}
                    onSubmit={handleWizardSubmit}
                    campaignData={campaignData}
                    systemParametersData={params.systemParametersData}
                />
            )}
        </div>
        </>
    );
};

export default CampaignDetailComponentV2;
